import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getReadiness } from './coachApi';

const SCORE_COLOR = (s) => {
  if (!s) return C.silver2;
  if (s >= 80) return '#4AC87A';
  if (s >= 60) return '#C8A84B';
  if (s >= 40) return '#D4873A';
  return '#C05050';
};

const SCORE_LABEL_EN = (s) => {
  if (!s) return 'No data';
  if (s >= 80) return 'High';
  if (s >= 60) return 'Moderate';
  if (s >= 40) return 'Low';
  return 'Critical';
};
const SCORE_LABEL_NL = (s) => {
  if (!s) return 'Geen data';
  if (s >= 80) return 'Hoog';
  if (s >= 60) return 'Matig';
  if (s >= 40) return 'Laag';
  return 'Kritiek';
};

function ReadinessRing({ score, size = 64 }) {
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const fill = score ? circ * (1 - score / 100) : circ;
  const color = SCORE_COLOR(score);
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`${color}30`} strokeWidth="5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={circ} strokeDashoffset={fill}
        strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
      <text x={size / 2} y={size / 2 + 5} textAnchor="middle" fill={color} fontSize="14" fontWeight="800">
        {score || '—'}
      </text>
    </svg>
  );
}

function HeatCell({ score }) {
  const bg = score
    ? score >= 80 ? '#4AC87A40' : score >= 60 ? '#C8A84B40' : score >= 40 ? '#D4873A40' : '#C0505040'
    : _T.bg4;
  const border = score ? SCORE_COLOR(score) + '60' : _T.cardBorderBot;
  return (
    <div style={{ width: 28, height: 28, borderRadius: 6, background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: score ? SCORE_COLOR(score) : C.silver2 }}>
      {score || '—'}
    </div>
  );
}

export default function CoachReadinessTab({ coachId, clients, lang, tier }) {
  const nl = lang === 'nl';
  const [readiness, setReadiness] = useState({});
  const [loading, setLoading]     = useState(true);
  const [activeClient, setActiveClient] = useState(null);

  const load = useCallback(() => {
    getReadiness(coachId).then(d => {
      if (d && typeof d === 'object') setReadiness(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const clientList = clients || [];

  // Build readiness entries — use API data if available, else derive from client wellness data
  const entries = clientList.map(c => {
    const apiData = readiness[c.user_id];
    // Composite score from sleep + mood if no API data
    let score = apiData?.score || null;
    if (!score && (c.avg_sleep || c.avg_mood)) {
      const sleepScore = c.avg_sleep ? Math.min((c.avg_sleep / 9) * 100, 100) : 60;
      const moodScore = c.avg_mood ? (c.avg_mood / 5) * 100 : 60;
      score = Math.round(sleepScore * 0.5 + moodScore * 0.5);
    }
    return {
      ...c,
      score,
      history: apiData?.history || [],
      components: apiData?.components || {
        sleep: c.avg_sleep ? Math.round(Math.min((c.avg_sleep / 9) * 100, 100)) : null,
        mood: c.avg_mood ? Math.round((c.avg_mood / 5) * 100) : null,
        hrv: apiData?.hrv || null,
        soreness: apiData?.soreness || null,
        motivation: apiData?.motivation || null,
      }
    };
  });

  const sorted = [...entries].sort((a, b) => (a.score || 0) - (b.score || 0));
  const critical = sorted.filter(e => e.score && e.score < 40);
  const low = sorted.filter(e => e.score && e.score >= 40 && e.score < 60);
  const moderate = sorted.filter(e => e.score && e.score >= 60 && e.score < 80);
  const high = sorted.filter(e => e.score && e.score >= 80);
  const noData = sorted.filter(e => !e.score);

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (activeClient) {
    const entry = entries.find(e => e.user_id === activeClient.user_id) || activeClient;
    const comp = entry.components || {};
    return (
      <div style={{ padding: '0 0 32px' }}>
        <button onClick={() => setActiveClient(null)} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 16px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <ReadinessRing score={entry.score} size={72} />
          <div>
            <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{activeClient.name}</div>
            <div style={{ fontSize: 14, color: SCORE_COLOR(entry.score), fontWeight: 700, marginTop: 2 }}>
              {nl ? SCORE_LABEL_NL(entry.score) : SCORE_LABEL_EN(entry.score)} {nl ? 'gereedheid' : 'readiness'}
            </div>
            {entry.score && (
              <div style={{ fontSize: 11, color: C.silver2, marginTop: 2 }}>
                {entry.score >= 80 ? (nl ? '✓ Klaar voor training' : '✓ Ready to train') :
                 entry.score >= 60 ? (nl ? '⚡ Lichte aanpassingen aanbevolen' : '⚡ Light adjustments recommended') :
                 entry.score >= 40 ? (nl ? '⚠ Overweeg rust/herstel' : '⚠ Consider rest/recovery') :
                 (nl ? '🛑 Training niet aanbevolen' : '🛑 Training not recommended')}
              </div>
            )}
          </div>
        </div>

        {/* Component breakdown */}
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'COMPONENTEN' : 'COMPONENTS'}</div>
          {[
            { key: 'sleep', icon: '😴', label: nl ? 'Slaap' : 'Sleep' },
            { key: 'mood', icon: '💚', label: nl ? 'Stemming' : 'Mood' },
            { key: 'hrv', icon: '❤️', label: 'HRV' },
            { key: 'soreness', icon: '💪', label: nl ? 'Spierpijn' : 'Soreness' },
            { key: 'motivation', icon: '🔥', label: nl ? 'Motivatie' : 'Motivation' },
          ].map(({ key, icon, label }) => {
            const val = comp[key];
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{icon}</div>
                <div style={{ width: 80, color: C.silver, fontSize: 12 }}>{label}</div>
                <div style={{ flex: 1, background: _T.bg4, borderRadius: 4, height: 8, overflow: 'hidden' }}>
                  {val && <div style={{ height: '100%', width: `${val}%`, background: SCORE_COLOR(val), borderRadius: 4, transition: 'width 0.4s' }} />}
                </div>
                <div style={{ width: 36, textAlign: 'right', color: val ? SCORE_COLOR(val) : C.silver2, fontSize: 12, fontWeight: 700 }}>{val || '—'}</div>
              </div>
            );
          })}
        </div>

        {/* 7-day heatmap */}
        {entry.history.length > 0 && (
          <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? '7-DAAGSE TREND' : '7-DAY TREND'}</div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
              {entry.history.slice(-7).map((score, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <HeatCell score={score} />
                  <div style={{ fontSize: 8, color: C.silver2 }}>-{entry.history.slice(-7).length - i - 1}d</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coach recommendation */}
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, marginTop: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>{nl ? 'COACH AANBEVELING' : 'COACH RECOMMENDATION'}</div>
          <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.6 }}>
            {!entry.score ? (nl ? 'Geen data beschikbaar. Vraag de atleet om zijn wellness bij te houden.' : 'No data available. Ask the athlete to log their wellness.') :
             entry.score >= 80 ? (nl ? 'Atleet is volledig hersteld. Hoge intensiteit training is geschikt. Overweeg PR-pogingen of zware sets.' : 'Athlete is fully recovered. High intensity training is appropriate. Consider PR attempts or heavy sets.') :
             entry.score >= 60 ? (nl ? 'Atleet is redelijk hersteld. Normale training met bewuste monitoring. Vermijd extreme volumes.' : 'Athlete is moderately recovered. Normal training with mindful monitoring. Avoid extreme volumes.') :
             entry.score >= 40 ? (nl ? 'Atleet toont tekenen van vermoeidheid. Aanbeveling: hersteltraining, yoga of lichte activiteit. Prioriteit aan slaap en voeding.' : 'Athlete shows fatigue signs. Recommend recovery training, yoga or light activity. Prioritize sleep and nutrition.') :
             (nl ? 'Atleet is onderhersteld. Sterk aanbevolen: rust dag of actief herstel. Onderzoek slaapkwaliteit en stressbronnen.' : 'Athlete is under-recovered. Strongly recommend: rest day or active recovery. Investigate sleep quality and stress sources.')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Gereedheidsmonitor' : 'Readiness Monitor'}</div>
      <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>{nl ? 'Dagelijkse trainingsbereidheid per atleet' : 'Daily training readiness per athlete'}</div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div>
      ) : clientList.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🎯</div>
          <div style={{ fontSize: 14 }}>{nl ? 'Nog geen atleten' : 'No athletes yet'}</div>
        </div>
      ) : (
        <>
          {/* Status overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 16 }}>
            {[
              { label: nl ? 'Hoog' : 'High', count: high.length, color: '#4AC87A' },
              { label: nl ? 'Matig' : 'Moderate', count: moderate.length, color: '#C8A84B' },
              { label: nl ? 'Laag' : 'Low', count: low.length, color: '#D4873A' },
              { label: nl ? 'Kritiek' : 'Critical', count: critical.length, color: '#C05050' },
            ].map(seg => (
              <div key={seg.label} style={{ background: _T.bg3, border: `1px solid ${seg.color}33`, borderRadius: 12, padding: '10px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: seg.color }}>{seg.count}</div>
                <div style={{ fontSize: 9, color: C.silver2, marginTop: 2 }}>{seg.label}</div>
              </div>
            ))}
          </div>

          {/* Critical alerts */}
          {critical.length > 0 && (
            <div style={{ background: '#C0505015', border: '1px solid #C0505044', borderRadius: 14, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#C05050', letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>🛑 {nl ? 'KRITIEKE STATUS' : 'CRITICAL STATUS'}</div>
              {critical.map(e => (
                <div key={e.user_id} onClick={() => setActiveClient(e)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 6 }}>
                  <ReadinessRing score={e.score} size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{e.name}</div>
                    <div style={{ color: '#C05050', fontSize: 10 }}>{nl ? 'Training niet aanbevolen' : 'Training not recommended'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Full athlete list */}
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 10 }}>{nl ? 'ALLE ATLETEN' : 'ALL ATHLETES'}</div>
          {[...critical, ...low, ...moderate, ...high, ...noData].map(e => (
            <div key={e.user_id} onClick={() => setActiveClient(e)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '12px 14px', marginBottom: 8, cursor: 'pointer' }}>
              <ReadinessRing score={e.score} size={52} />
              <div style={{ flex: 1 }}>
                <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{e.name}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {e.avg_sleep && <span style={{ fontSize: 10, color: '#5B9ED0' }}>😴 {e.avg_sleep}h</span>}
                  {e.avg_mood && <span style={{ fontSize: 10, color: '#4AC87A' }}>💚 {e.avg_mood}/5</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: SCORE_COLOR(e.score) }}>
                  {nl ? SCORE_LABEL_NL(e.score) : SCORE_LABEL_EN(e.score)}
                </div>
                <div style={{ fontSize: 10, color: C.silver2 }}>›</div>
              </div>
            </div>
          ))}

          {/* Heatmap grid */}
          {entries.some(e => e.history.length > 0) && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 12 }}>{nl ? '7-DAAGSE HEATMAP' : '7-DAY HEATMAP'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {entries.filter(e => e.history.length > 0).map(e => (
                  <div key={e.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 70, color: C.silver, fontSize: 11, fontWeight: 600 }}>{e.name?.split(' ')[0]}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {e.history.slice(-7).map((score, i) => <HeatCell key={i} score={score} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
